import type { Scan } from "@hobbyshopify/backend-client";
import { useId, useRef, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { BottomBar, useSaidInView } from "../../components/BottomBar";
import { CardPhotos, PHOTOS_BESIDE, PHOTOS_SPAN } from "../../components/CardPhotos";
import { CatalogCodes } from "../../components/CatalogCodes";
import { EbayGradeLinks } from "../../components/EbayGradeLinks";
import { Hint, MoreDetailsToggle, SectionTitle, Switch, TextField } from "../../components/EditorPieces";
import { GradeCombobox } from "../../components/GradeCombobox";
import { gridGradeKey } from "../../components/grade-grid";
import { LinkSealedPicker } from "../../components/LinkSealedPicker";
import { MarketEstimatePanel } from "../../components/MarketEstimatePanel";
import { PricePanel } from "../../components/PricePanel";
import { CheckIcon, ChevronLeftIcon, CrossIcon, RefreshIcon } from "../../components/review-icons";
import { ScanCandidates } from "../../components/ScanCandidates";
import { describeCandidate, isScannerPick, selectedProductId } from "../../core/primitives/candidates";
import { cardInfoTitle, formatCents } from "../../core/primitives/card-display";
import { formatWhen } from "../../core/primitives/format";
import { estimateForGrade } from "../../core/primitives/grades";
import { needsCandidatePick } from "../../core/primitives/review-row";
import { inputToCents, isMoneyValid, isYearValid, MAX_YEAR, MIN_YEAR } from "../../core/primitives/scan-edit";
import {
  COMMON_SPORTS,
  SEALED_CATEGORY_LABELS,
  SEALED_PRODUCT_TYPE_LABELS,
  type SealedEditState,
  scanBarcode,
  scanMarketCents,
  scannerReadings,
  sealedGuides,
} from "../../core/primitives/sealed-edit";
import { ScanEditorActions, ScanEditorState } from "./scan-editor-model";

const CARD_TYPES = [
  { value: "sports", label: "Sports" },
  { value: "pokemon", label: "Pokémon" },
  { value: "mtg", label: "Magic: The Gathering" },
  { value: "yugioh", label: "Yu-Gi-Oh!" },
  { value: "comic", label: "Comics" },
];

/** Where the page that shows the editor sits in a queue of scans, and its way back. */
export interface ScanEditorHeader {
  readonly onBack: () => void;
  /** "2 of 5", when opened from a list of scans to walk. */
  readonly position: { readonly index: number; readonly total: number } | null;
  /** Moves on without deciding this one, while more wait. */
  readonly onSkip: (() => void) | null;
}

/** The matched card or item and what confirming does to it, in one line. */
function matchNote(scan: Scan, noun: string): string {
  if (scan.matchKind === "NONE") return `Not in your catalogue — confirming creates a new ${noun}.`;
  const confirmedAt = noun === "card" ? scan.card?.confirmedAt : scan.item?.confirmedAt;
  return confirmedAt == null
    ? `Matches a ${noun} nobody has reviewed yet.`
    : `Matches a ${noun} already in your catalogue.`;
}

const matchesStock = (scan: Scan) => (scan.matchKind === "PRODUCT" || scan.matchKind === "IDENTITY") && !!scan.card;

const decidedNote = (scan: Scan) =>
  scan.review === "REJECTED"
    ? "This scan was rejected."
    : "This scan was already confirmed. Its price and stock are edited on its card, under Stock.";

/**
 * One scan, reviewed in full: the photo beside the product it was identified
 * as, the look-alikes to pick between, the price and copies to confirm with,
 * the reading to correct, the comps for every grade, and where the copies go.
 * Sealed product swaps the grades for its market price and its product details.
 * A port of the iOS `AdminScanEditView`. A scan already decided opens read-only.
 */
export function ScanEditor({
  header,
  secondary,
}: {
  header?: ScanEditorHeader;
  /** In place of Reject: the scan page's own way out ("Start over"). */
  secondary?: { label: string; onClick: () => void };
}) {
  const state = useSubtree(ScanEditorState);
  const actions = useSubtree(ScanEditorActions);
  const observer = useObserver();
  const [showMore, setShowMore] = useState(false);
  const gradeId = useId();
  const categoryId = useId();
  const blockerId = useId();
  const notesRef = useRef<HTMLDivElement>(null);

  const scan = observer.watch(state.scan);
  const edit = observer.watch(state.edit);
  const sealed = observer.watch(state.sealedEdit);
  const busy = observer.watch(state.busy);
  const saving = observer.watch(state.saving);
  const draftPending = observer.watch(state.draftPending);
  const draftError = observer.watch(state.draftError);
  const savingDetails = observer.watch(state.savingDetails);
  const needsReprice = observer.watch(state.needsReprice);
  const repriceNote = observer.watch(state.repriceNote);
  const picking = observer.watch(state.picking);
  const searching = observer.watch(state.searching);
  const priceManual = observer.watch(state.priceManual);
  const marketRefresh = observer.watch(state.marketRefresh);
  const staleMatchWarning = observer.watch(state.staleMatchWarning);
  const approvedCodes = observer.watch(state.approvedCodes);
  const catalogPicking = observer.watch(state.catalogPicking);
  const catalogNotice = observer.watch(state.catalogNotice);
  const error = observer.watch(state.error);
  const canConfirm = observer.watch(state.canConfirm);
  const confirmBlocker = observer.watch(state.confirmBlocker);
  const separateFloor = observer.watch(state.separateFloor);
  const quantity = observer.watch(state.quantity);
  const certNumber = observer.watch(state.certNumber);
  const costPerCopy = observer.watch(state.costPerCopy);
  const stockLocationId = observer.watch(state.stockLocationId);
  const locations = observer.watch(state.locations);
  const quoteValidityMinutes = observer.watch(state.quoteValidityMinutes);
  const linkable = observer.watch(state.linkable);
  const linkTarget = observer.watch(state.linkTarget);
  const linkSearch = observer.watch(state.linkSearch);
  const linkStatus = observer.watch(state.linkStatus);
  const linkCandidates = observer.watch(state.linkCandidates);
  const linkConflict = observer.watch(state.linkConflict);
  const linkMatched = observer.watch(state.linkMatched);
  const linkAdoptName = observer.watch(state.linkAdoptName);
  const linkAdoptPrice = observer.watch(state.linkAdoptPrice);

  const pending = scan?.review === "PENDING";
  // A write's own failure, when it is not the decision's sentence already.
  const writeError = draftError !== null && draftError !== error ? draftError : null;
  // A decided scan says so at the top, so one decided while it was open comes into view saying so.
  const decided = scan && !pending ? decidedNote(scan) : null;
  useSaidInView(
    notesRef,
    [error, writeError, decided].filter(Boolean).join(" ") || null,
    !pending,
    busy || saving || savingDetails || searching || picking !== null || catalogPicking !== null,
  );

  const form = sealed ?? edit;
  if (!scan || !form) return null;

  const noun = sealed ? "item" : "card";
  const customer = scan.kind === "CUSTOMER";
  const pickedId = selectedProductId(scan.scpData);
  const picked = scan.candidates.find((c) => pickedId !== null && describeCandidate(c.product).productId === pickedId);
  const catalog = picked ? `Catalog · ${describeCandidate(picked.product).parallel || "base"}` : null;
  const catalogPhoto = picked?.images[0] ?? null;
  const priceCents = inputToCents(form.price);
  const approveTitle = `${customer ? "Quote" : "Approve"}${priceCents === undefined ? "" : ` ${formatCents(priceCents)}`}`;
  const confirmTitle = linkTarget ? (quantity > 0 ? `Link and add ${quantity}` : "Link only") : approveTitle;
  const guides = sealed ? sealedGuides(scan.scpData) : null;
  const inFlight = busy || savingDetails || saving || draftPending || needsReprice || searching;

  const noEstimate = sealed
    ? guides?.marketCents == null
    : scan.estimatedPriceCents === null && scan.card?.currentPriceCents == null;
  const estimatedFrom = !sealed && scan.card?.currentPriceCents == null ? scan.estimatedPriceFrom : null;

  const priceNotes = (
    <div className="space-y-1.5" aria-live="polite">
      {needsReprice ? (
        <Hint warn>Card details changed. These prices are for the old card until you save and reprice.</Hint>
      ) : repriceNote ? (
        <p className="text-xs text-warn">
          {repriceNote.text}{" "}
          {repriceNote.previous !== null && (
            <button type="button" onClick={actions.undoReprice} className="font-semibold text-accent">
              Undo
            </button>
          )}
        </p>
      ) : sealed ? (
        noEstimate ? (
          <Hint warn>We couldn't price this product automatically. Type a price.</Hint>
        ) : (
          <Hint>
            {priceManual
              ? "Your price — a new product won't change it. Clear it to follow the estimate."
              : "Follows the estimate until you type a price."}
          </Hint>
        )
      ) : noEstimate ? (
        <Hint warn>We couldn't price this grade automatically. Pick a grade with comps, or type a price.</Hint>
      ) : (
        estimatedFrom && <Hint warn>Estimated from {estimatedFrom} comps — this grade has none of its own.</Hint>
      )}
      {saving ? (
        <p className="text-xs text-faint">Saving…</p>
      ) : (
        pending && (
          <p className="text-xs text-faint">
            {linkTarget ? "Confirming links this scan to the item you picked." : matchNote(scan, noun)}
          </p>
        )
      )}
      {customer && quoteValidityMinutes !== null && (
        <p className="text-xs text-faint">
          Confirming quotes this price to the customer for {quoteValidityMinutes} minutes, and holds a copy for them
          until then.
        </p>
      )}
    </div>
  );

  const certField = !customer && !sealed && (
    <TextField
      label="Cert number"
      value={certNumber}
      onChange={actions.setCertNumber}
      placeholder="Optional"
      inputMode="numeric"
      readOnly={!pending}
    />
  );

  const cardDetails = edit && (
    <section className="space-y-3">
      <SectionTitle>Card details</SectionTitle>
      <TextField
        label="Parallel"
        value={edit.parallelType}
        onChange={(parallelType) => actions.change({ parallelType })}
        placeholder="Base"
        readOnly={!pending}
      />
      <TextField
        label="Year"
        value={edit.year}
        onChange={(year) => actions.change({ year: year.replace(/\D/g, "").slice(0, 4) })}
        placeholder="2021"
        inputMode="numeric"
        error={isYearValid(edit.year) ? null : `Enter a 4-digit year between ${MIN_YEAR} and ${MAX_YEAR}.`}
        readOnly={!pending}
      />
      <div>
        <label htmlFor={gradeId} className="mb-1 block text-xs font-medium text-muted">
          Grade
        </label>
        <GradeCombobox
          id={gradeId}
          value={edit.grade}
          onType={(grade) => actions.change({ grade })}
          onCommit={actions.commitGrade}
          onPick={actions.pickGrade}
          scpData={scan.scpData}
          placeholder="PSA 10, 9.5, raw…"
          disabled={!pending}
        />
      </div>
      <MoreDetailsToggle open={showMore} onToggle={() => setShowMore((open) => !open)} />
      {showMore && (
        <div className="space-y-3">
          <Hint>Correcting anything here re-matches the scan, and may make it a different card.</Hint>
          <TextField
            label="Card name"
            value={edit.cardFullName}
            onChange={(cardFullName) => actions.change({ cardFullName })}
            placeholder="2021 Prizm Ja Morant #42"
            readOnly={!pending}
          />
          <div className="flex gap-3">
            <TextField
              label="Player"
              value={edit.player}
              onChange={(player) => actions.change({ player })}
              placeholder="Ja Morant"
              readOnly={!pending}
            />
            <TextField
              label="Card #"
              value={edit.cardNumber}
              onChange={(cardNumber) => actions.change({ cardNumber })}
              placeholder="42"
              readOnly={!pending}
            />
          </div>
          <div className="flex gap-3">
            <TextField
              label="Set"
              value={edit.setName}
              onChange={(setName) => actions.change({ setName })}
              placeholder="Prizm"
              readOnly={!pending}
            />
            <TextField
              label="Insert"
              value={edit.insertName}
              onChange={(insertName) => actions.change({ insertName })}
              placeholder="Downtown"
              readOnly={!pending}
            />
          </div>
          <div className="flex gap-3">
            <TextField
              label="Team"
              value={edit.team}
              onChange={(team) => actions.change({ team })}
              placeholder="Grizzlies"
              readOnly={!pending}
            />
            <TextField
              label="Brand"
              value={edit.brand}
              onChange={(brand) => actions.change({ brand })}
              placeholder="Panini"
              readOnly={!pending}
            />
          </div>
          <div className="flex gap-3">
            <div className="min-w-0 flex-1">
              <label htmlFor={categoryId} className="mb-1 block text-xs font-medium text-muted">
                Category
              </label>
              <select
                id={categoryId}
                className="field"
                value={edit.cardType}
                disabled={!pending}
                onChange={(e) => actions.change({ cardType: e.target.value })}
              >
                <option value="">Unspecified</option>
                {CARD_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {certField}
          </div>
          <Switch label="Slabbed" checked={edit.slabbed} onChange={actions.setSlabbed} disabled={!pending} />
        </div>
      )}
    </section>
  );

  const details = sealed ? (
    <SealedDetails
      form={sealed}
      scanner={scannerReadings(scan, sealed)}
      barcode={scanBarcode(scan)}
      pending={pending}
      showMore={showMore}
      onToggleMore={() => setShowMore((open) => !open)}
      onChange={actions.changeSealed}
    />
  ) : (
    cardDetails
  );

  // Which comp the grid marks: computed from the grade being edited, so
  // correcting the grade moves the mark with it — and only while the estimate is
  // what is in the price field, since a matched card's own price came from the
  // shop's earlier decision, not from a comp in this grid.
  // Sealed product has no grades: one price, and a sold-listings search for it.
  const comps = edit ? (
    <EbayGradeLinks
      searchLinks={scan.searchLinks ?? undefined}
      scpData={scan.scpData}
      pricedGradeKey={
        scan.card?.currentPriceCents == null ? estimateForGrade(edit.grade, edit.slabbed, scan.scpData).gradeKey : null
      }
      cardGradeKey={gridGradeKey(edit.grade, edit.slabbed)}
      cardTitle={edit.cardFullName.trim() || cardInfoTitle(scan.cardInfo)}
    />
  ) : (
    scan.searchLinks?.sealed && (
      <a href={scan.searchLinks.sealed} target="_blank" rel="noreferrer" className="btn-secondary h-11 w-full">
        Sold listings on eBay
      </a>
    )
  );

  const subtitle = [
    header?.position ? `${header.position.index} of ${header.position.total}` : "",
    `scanned ${formatWhen(scan.timestamp)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  // A box identified by its barcode alone has no photo to show.
  const missing = sealed ? "Identified by barcode, no photo" : undefined;
  const mustPick = needsCandidatePick(scan);
  const matchedItem = sealed && scan.matchKind !== "NONE" && scan.matchKind !== "CREATED" ? scan.item : null;
  const stale = sealed && scan.searchStaleBecause.length > 0;

  return (
    <div className={`${pending ? "pb-36" : "pb-10"} ${PHOTOS_BESIDE}`}>
      {header && (
        <header className={`mb-4 flex items-center gap-1 ${PHOTOS_SPAN}`}>
          <button
            type="button"
            onClick={header.onBack}
            aria-label="Back to review"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-btn text-white hover:bg-surface"
          >
            <ChevronLeftIcon />
          </button>
          <div className="min-w-0 flex-1 text-center">
            <h1 className="text-[17px] font-semibold text-white">{pending ? "Review scan" : "Scan"}</h1>
            <p className="truncate text-xs text-muted">{subtitle}</p>
          </div>
          <span className="flex w-[52px] shrink-0 justify-end">
            {header.onSkip && pending && (
              <button type="button" onClick={header.onSkip} className="px-1 text-base text-accent">
                Skip
              </button>
            )}
          </span>
        </header>
      )}

      <div className="mb-6 lg:mb-0">
        <CardPhotos
          shown={[
            { url: scan.imageUrl, caption: "Your scan · front", missing },
            catalog && catalogPhoto
              ? { url: catalogPhoto, caption: catalog }
              : { url: scan.backImageUrl, caption: "Your scan · back", missing },
          ]}
          gallery={[
            { url: scan.imageUrl, caption: "Your scan · front" },
            ...(picked && catalog ? picked.images.map((url) => ({ url, caption: catalog })) : []),
            { url: scan.backImageUrl, caption: "Your scan · back" },
          ]}
        />
      </div>

      <div className="space-y-6">
        {/* While the bar is up, what an action answers shows in it, where the reviewer is looking. */}
        {decided && (
          <div ref={notesRef} className="scroll-mt-12 space-y-2.5">
            {error && (
              <p role="alert" className="note note-danger text-danger">
                {error}
              </p>
            )}
            {writeError && (
              <p role="alert" className="note note-warn text-warn">
                {writeError}
              </p>
            )}
            <p className="note note-neutral text-muted">{decided}</p>
          </div>
        )}

        {pending ? (
          <>
            {(catalogNotice || scan.catalog || scan.suggestedCodes.length > 0) && (
              <section className="space-y-2.5">
                <SectionTitle warn={scan.catalog?.status === "CONFLICT"}>Barcode</SectionTitle>
                {catalogNotice && <p className="note note-warn text-sm">{catalogNotice}</p>}
                <CatalogCodes
                  catalog={scan.catalog}
                  codes={scan.suggestedCodes}
                  approved={approvedCodes}
                  picking={catalogPicking}
                  disabled={busy || catalogPicking !== null}
                  onToggle={actions.toggleCode}
                  onPick={(id) => void actions.pickCatalog(id)}
                />
              </section>
            )}

            {(scan.candidates.length > 0 || scan.card || matchedItem) && (
              <section className="space-y-2.5">
                <SectionTitle warn={mustPick}>{mustPick ? "Pick the right match" : "Identified as"}</SectionTitle>
                {!picked && scan.item && <p className="text-sm font-semibold text-white">{scan.item.title}</p>}
                {sealed && picked && (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-white">{describeCandidate(picked.product).title}</span>
                    {guides?.marketCents != null && (
                      <span className="text-muted">· Market {formatCents(guides.marketCents)}</span>
                    )}
                    <span className="badge badge-neutral">
                      {isScannerPick(scan.candidates, scan.scpData) ? "Scanner's pick" : "Your pick"}
                    </span>
                  </div>
                )}
                {stale && (
                  <div className="note note-warn flex items-center justify-between gap-3 text-sm">
                    <span>{staleMatchWarning ?? "Identity changed since the last search."}</span>
                    <button
                      type="button"
                      onClick={() => void actions.searchAgain()}
                      disabled={inFlight || picking !== null}
                      className="btn-secondary shrink-0"
                    >
                      {searching ? "Searching…" : "Search again"}
                    </button>
                  </div>
                )}
                {scan.candidates.length > 0 && (
                  <ScanCandidates
                    candidates={scan.candidates}
                    selectedProductId={pickedId}
                    picking={picking}
                    disabled={inFlight}
                    onPick={(productId) => void actions.pick(productId)}
                    sealed={sealed !== null}
                  />
                )}
                {matchedItem ? (
                  <p className="note note-accent text-accent">
                    Already in stock: {matchedItem.trackStock ? `${matchedItem.available} on hand` : "untracked"}
                    {matchedItem.currentPriceCents != null && ` at ${formatCents(matchedItem.currentPriceCents)}`}.
                    {customer ? "Confirming quotes this item." : "Confirming adds to that item."}
                  </p>
                ) : matchesStock(scan) && scan.card ? (
                  <p className="note note-accent text-accent">
                    Already in stock: {scan.card.trackStock ? `${scan.card.available} on hand` : "untracked"}
                    {scan.card.currentPriceCents != null && ` at ${formatCents(scan.card.currentPriceCents)}`}.
                    {customer ? "Confirming quotes this card." : "Confirming adds these copies to that card."}
                  </p>
                ) : (
                  scan.matchKind === "NONE" &&
                  !linkTarget && (
                    <p className="text-xs text-muted">Not in your stock yet — confirming creates the {noun}.</p>
                  )
                )}
              </section>
            )}

            {sealed && linkable && (
              <LinkSealedPicker
                search={linkSearch}
                status={linkStatus}
                candidates={linkCandidates}
                matched={linkMatched}
                scanName={sealed.productFullName.trim() || null}
                marketCents={scanMarketCents(scan)}
                adoptName={linkAdoptName}
                adoptPrice={linkAdoptPrice}
                onToggleName={actions.toggleLinkName}
                onTogglePrice={actions.toggleLinkPrice}
                selected={linkTarget}
                conflict={linkConflict}
                scan={{ ...sealed, barcode: scanBarcode(scan) }}
                disabled={busy}
                onSearch={actions.searchLink}
                onPick={actions.pickLink}
                onClear={actions.clearLink}
              />
            )}

            {sealed && (
              <MarketEstimatePanel
                estimate={scan.marketEstimate}
                refresh={marketRefresh}
                onRefresh={() => void actions.refreshMarket()}
                onUse={actions.useMarketPrice}
              />
            )}

            <PricePanel
              price={form.price}
              onPrice={actions.setPrice}
              count={
                customer
                  ? undefined
                  : {
                      value: quantity,
                      onChange: actions.setQuantity,
                      label: "Copies",
                      zeroLabel: linkTarget ? "None" : "Untracked",
                    }
              }
              separateFloor={separateFloor}
              onSeparateFloor={actions.setSeparateFloor}
              floor={form.floorPrice}
              onFloor={actions.setFloor}
              floorError={separateFloor && !floorValid(form) ? "The floor cannot be above the asking price." : null}
            >
              {priceNotes}
            </PricePanel>

            {details}
            {comps}

            {!customer && (linkTarget || quantity > 0) && (
              <section className="space-y-2.5">
                <SectionTitle>Stock</SectionTitle>
                {linkTarget && (
                  <Hint>Linking alone adds no stock. Add copies only for boxes you are taking in now.</Hint>
                )}
                {quantity > 0 && locations.length > 1 && (
                  <div className="flex flex-wrap gap-2">
                    {locations.map((location) => {
                      const chosen = location.id === stockLocationId;
                      return (
                        <button
                          key={location.id}
                          type="button"
                          aria-pressed={chosen}
                          onClick={() => actions.setStockLocation(location.id)}
                          className={`h-8 rounded-full border px-3 text-[13px] ${
                            chosen
                              ? "border-accent bg-accent font-semibold text-ink"
                              : "border-accent/55 font-medium text-accent"
                          }`}
                        >
                          {location.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                {quantity > 0 && (
                  <TextField
                    label="Cost per copy (optional)"
                    value={costPerCopy}
                    onChange={actions.setCostPerCopy}
                    placeholder="What the shop paid for each one"
                    inputMode="decimal"
                    error={isMoneyValid(costPerCopy) ? null : "Enter an amount, or leave it blank."}
                  />
                )}
              </section>
            )}
          </>
        ) : (
          <>
            {comps}
            {details}
            {/* With no bar, the scan page's way out stays. */}
            {secondary && (
              <button type="button" onClick={secondary.onClick} disabled={busy} className="btn-secondary h-12 w-full">
                {secondary.label}
              </button>
            )}
          </>
        )}
      </div>

      {pending && (
        <BottomBar>
          {error && (
            <p role="alert" className="mb-1.5 text-xs text-danger">
              {error}
            </p>
          )}
          {writeError && (
            <p role="alert" className="mb-1.5 text-xs text-warn">
              {writeError}
            </p>
          )}
          {confirmBlocker && !needsReprice && (
            <p id={blockerId} className="field-error mb-2 text-xs">
              {confirmBlocker}
            </p>
          )}
          <div className="flex gap-2.5">
            {secondary ? (
              <button type="button" onClick={secondary.onClick} disabled={busy} className="btn-secondary h-12 px-4">
                {secondary.label}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void actions.reject()}
                disabled={busy}
                aria-label="Reject this scan"
                className="flex h-12 items-center gap-1.5 rounded-btn border border-danger/35 px-4 text-[15px] font-semibold text-danger disabled:opacity-50"
              >
                <CrossIcon />
                Reject
              </button>
            )}
            {needsReprice || savingDetails ? (
              <button
                type="button"
                onClick={() => void actions.saveAndReprice()}
                disabled={savingDetails || saving || busy || !isYearValid(form.year) || picking !== null}
                className="btn-primary flex h-12 flex-1 items-center justify-center gap-2"
              >
                <RefreshIcon />
                {savingDetails ? "Repricing…" : "Save and reprice"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void actions.confirm()}
                disabled={!canConfirm}
                aria-describedby={confirmBlocker ? blockerId : undefined}
                className="btn-primary flex h-12 flex-1 items-center justify-center gap-2"
              >
                <CheckIcon />
                {busy ? "Confirming…" : confirmTitle}
              </button>
            )}
          </div>
        </BottomBar>
      )}
    </div>
  );
}

function floorValid(form: { price: string; floorPrice: string }): boolean {
  const price = inputToCents(form.price);
  const floor = inputToCents(form.floorPrice);
  return price == null || floor == null || floor <= price;
}

/** What a sealed scan read, to correct: set, box type and variant are which product it is. */
function SealedDetails({
  form,
  scanner,
  barcode,
  pending,
  showMore,
  onToggleMore,
  onChange,
}: {
  form: SealedEditState;
  /** What the scanner read for each field the reviewer changed. */
  scanner: Partial<Record<keyof SealedEditState, string>>;
  barcode: string | null;
  pending: boolean;
  showMore: boolean;
  onToggleMore: () => void;
  onChange: (updates: Partial<SealedEditState>) => void;
}) {
  const categoryId = useId();
  const typeId = useId();
  const sportsId = useId();
  const was = (field: keyof SealedEditState) =>
    scanner[field] !== undefined && <p className="mt-1 text-xs text-faint">Scanner: {scanner[field]}</p>;
  const text = (field: keyof SealedEditState, label: string, placeholder?: string) => (
    <div className="min-w-0 flex-1">
      <TextField
        label={label}
        value={form[field]}
        onChange={(value) => onChange({ [field]: value })}
        placeholder={placeholder}
        readOnly={!pending}
      />
      {was(field)}
    </div>
  );
  const select = (id: string, field: "category" | "productType", label: string, options: Record<string, string>) => (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted">
        {label}
      </label>
      <select
        id={id}
        className="field"
        value={form[field]}
        disabled={!pending}
        onChange={(e) => onChange({ [field]: e.target.value })}
      >
        {Object.entries(options).map(([value, name]) => (
          <option key={value} value={value}>
            {name}
          </option>
        ))}
      </select>
      {was(field)}
    </div>
  );

  return (
    <section className="space-y-3">
      <SectionTitle>Product details</SectionTitle>
      <div className="flex gap-3">
        {select(typeId, "productType", "Product type", SEALED_PRODUCT_TYPE_LABELS)}
        {text("productVariant", "Variant", "Logofractor, Pokémon Center…")}
      </div>
      <div className="min-w-0">
        <TextField
          label="Set"
          value={form.setName}
          onChange={(setName) => onChange({ setName })}
          placeholder="Chrome, Prizm, Surging Sparks…"
          error={form.setName.trim() ? null : "Enter the set."}
          readOnly={!pending}
        />
        {was("setName")}
      </div>
      <div className="min-w-0">
        <TextField
          label="Year"
          value={form.year}
          onChange={(year) => onChange({ year: year.replace(/\D/g, "").slice(0, 4) })}
          placeholder="Blank for none printed"
          inputMode="numeric"
          error={isYearValid(form.year) ? null : `Enter a year from ${MIN_YEAR} to ${MAX_YEAR}, or leave it blank.`}
          readOnly={!pending}
        />
        {was("year")}
      </div>
      <Hint>Set, product type and variant are which product this is — a hobby box and a blaster are priced apart.</Hint>
      <MoreDetailsToggle open={showMore} onToggle={onToggleMore} />
      {showMore && (
        <div className="space-y-3">
          <div>
            {text("productFullName", "Product name")}
            <p className="mt-1 text-xs text-faint">
              How this product reads in lists. Editing it does not change the match.
            </p>
          </div>
          <div className="flex gap-3">
            {select(categoryId, "category", "Category", SEALED_CATEGORY_LABELS)}
            {form.category === "sports" && (
              <div className="min-w-0 flex-1">
                <label htmlFor={sportsId} className="mb-1 block text-xs font-medium text-muted">
                  Sport
                </label>
                <input
                  id={sportsId}
                  className="field"
                  list={`${sportsId}-list`}
                  value={form.sport}
                  readOnly={!pending}
                  onChange={(e) => onChange({ sport: e.target.value })}
                />
                <datalist id={`${sportsId}-list`}>
                  {COMMON_SPORTS.map((sport) => (
                    <option key={sport} value={sport} />
                  ))}
                </datalist>
                {was("sport")}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {text("brand", "Brand")}
            {text("language", "Language")}
          </div>
          {text("configuration", "Configuration", "12 packs x 4 cards")}
          {barcode && (
            <div className="min-w-0">
              <TextField label="Barcode" value={barcode} onChange={() => {}} readOnly />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
